import { normalize } from '../core/vec';
import type { Enemy } from './enemies';
import type { Engine } from './engine';

type Pattern = 'radial' | 'charge' | 'summon' | 'spiral';

const BOSS1_PATTERNS: Pattern[] = ['radial', 'charge', 'summon'];
const BOSS2_PATTERNS: Pattern[] = ['radial', 'charge', 'summon', 'spiral'];

interface BossCfg {
  chaseTime: number;
  radialN: number;
  summonN: number;
  boltSpeed: number;
  telegraph: number;
  dashSpeed: number;
  dashTime: number;
}

const CFG: Record<1 | 2, BossCfg> = {
  1: {
    chaseTime: 2.2, radialN: 12, summonN: 4,
    boltSpeed: 240, telegraph: 0.6, dashSpeed: 520, dashTime: 0.7,
  },
  2: {
    chaseTime: 1.8, radialN: 18, summonN: 6,
    boltSpeed: 270, telegraph: 0.55, dashSpeed: 580, dashTime: 0.65,
  },
};

function tier(e: Enemy): 1 | 2 {
  return e.kind === 'boss1' ? 1 : 2;
}

function pickPattern(engine: Engine, e: Enemy): Pattern {
  const list = tier(e) === 1 ? BOSS1_PATTERNS : BOSS2_PATTERNS;
  return engine.streams.boss.pick(list);
}

function fireRing(engine: Engine, e: Enemy, n: number, speed: number): void {
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    engine.fireBossBolt(a, speed, e, 'radial');
  }
}

function enterSkill(engine: Engine, e: Enemy, p: Pattern): void {
  e.state = 'skill';
  e.stateT = 0;
  e.affixes.push(p);
  const cfg = CFG[tier(e)];
  if (p === 'radial') fireRing(engine, e, cfg.radialN, cfg.boltSpeed);
  if (p === 'summon') engine.summonMinions(e, cfg.summonN);
  if (p === 'charge') {
    const n = normalize(engine.player.x - e.x, engine.player.y - e.y);
    e.dirX = n.x; e.dirY = n.y;
  }
}

function skillDuration(p: Pattern, cfg: BossCfg): number {
  if (p === 'charge') return cfg.telegraph + cfg.dashTime;
  if (p === 'spiral') return 2.4;
  return 0.8;
}

function updateSkill(
  engine: Engine,
  e: Enemy,
  p: Pattern,
  cfg: BossCfg,
  dt: number,
): void {
  const t = e.stateT;
  if (p === 'charge') {
    if (t > cfg.telegraph) {
      e.x += e.dirX * cfg.dashSpeed * dt;
      e.y += e.dirY * cfg.dashSpeed * dt;
    }
  }
  if (p === 'spiral') {
    const last = Math.floor((t - dt) / 0.12);
    const nowI = Math.floor(t / 0.12);
    for (let i = last + 1; i <= nowI; i++) {
      for (let k = 0; k < 3; k++) {
        const a = i * 0.5 + (k / 3) * Math.PI * 2;
        engine.fireBossBolt(a, cfg.boltSpeed, e, 'spiral');
      }
    }
  }
}

/** Boss 逐帧行为（由 enemies.updateEnemy 分发）。 */
export function updateBoss(e: Enemy, engine: Engine, dt: number): void {
  const t = tier(e);
  const cfg = CFG[t];
  const enraged = t === 2 && e.hp / e.maxHp < 0.4;
  const speedMul = enraged ? 1.25 : 1;

  if (e.state === 'approach') {
    const n = normalize(engine.player.x - e.x, engine.player.y - e.y);
    e.x += n.x * e.speed * speedMul * dt;
    e.y += n.y * e.speed * speedMul * dt;
    if (e.stateT >= cfg.chaseTime / speedMul) {
      enterSkill(engine, e, pickPattern(engine, e));
    }
    return;
  }

  if (e.state === 'skill') {
    const p = e.affixes[0] as Pattern | undefined;
    if (!p) { e.state = 'approach'; e.stateT = 0; return; }
    updateSkill(engine, e, p, cfg, dt);
    if (e.stateT >= skillDuration(p, cfg)) {
      e.state = 'approach';
      e.stateT = 0;
      e.affixes.length = 0;
    }
  }
}
